import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FaBuilding, FaSpinner, FaCheck, FaEdit, FaShieldAlt, FaClipboardCheck
} from 'react-icons/fa';
import { companyProfile } from '../api';
import { DEFAULT_COMPANY } from '../config/CompanyDetails';

// ============================================
// COMPANY PROFILE TAB (under Settings)
// Single source of truth for One Purpose company + bank details.
// Editing requires: passcode + approval by a DIFFERENT super-admin.
// ============================================

const FIELD_ORDER = [
  'company_name', 'company_short_name', 'company_reg_number', 'company_vat_number',
  'company_phone', 'company_email', 'company_website', 'company_address',
  'company_address_line2', 'company_logo',
  'bank_account_name', 'bank_name', 'bank_account_number', 'bank_account_type', 'bank_branch_code'
];

const CompanyProfileTab = ({ user }) => {
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [passcode, setPasscode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Approvals
  const [changes, setChanges] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewPasscode, setReviewPasscode] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, changesRes] = await Promise.all([companyProfile.get(), companyProfile.getChanges()]);
      if (profileRes.success) setProfile(profileRes.profile || {});
      if (changesRes.success) setChanges(changesRes.changes || []);
    } catch (e) {
      console.error('Company profile load error:', e);
      toast.error('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Logo: use the DB value, fall back to the single-source default, and
  // resolve relative paths to absolute so the image actually renders.
  const rawLogo = profile.company_logo || DEFAULT_COMPANY.company_logo || '';
  const logoImg = rawLogo
    ? (rawLogo.startsWith('http://') || rawLogo.startsWith('https://') || rawLogo.startsWith('data:')
        ? rawLogo
        : (typeof window !== 'undefined' ? window.location.origin + rawLogo : rawLogo))
    : '';

  const openEdit = () => {
    setEditData({ ...profile });
    setPasscode('');
    setEditOpen(true);
  };

  const submitChange = async () => {
    if (!passcode.trim()) {
      toast.error('Enter the company passcode to submit changes');
      return;
    }
    const changesPayload = FIELD_ORDER
      .filter((k) => String(editData[k] ?? '') !== String(profile[k] ?? ''))
      .map((k) => ({ key: k, new_value: editData[k] || '' }));
    if (changesPayload.length === 0) {
      toast.error('No changes made');
      return;
    }
    setSubmitting(true);
    try {
      const res = await companyProfile.submitChange(changesPayload, passcode);
      if (res.success) {
        toast.success(res.message);
        setEditOpen(false);
        load();
      } else {
        toast.error(res.error || 'Failed to submit change');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to submit change');
    } finally {
      setSubmitting(false);
    }
  };

  const review = async (action) => {
    if (!reviewPasscode.trim()) {
      toast.error('Enter the company passcode');
      return;
    }
    setReviewing(true);
    try {
      const res = action === 'approve'
        ? await companyProfile.approveChange(reviewTarget.id, reviewPasscode)
        : await companyProfile.rejectChange(reviewTarget.id, reviewPasscode, reviewNotes);
      if (res.success) {
        toast.success(res.message);
        setReviewTarget(null);
        setReviewPasscode('');
        setReviewNotes('');
        load();
      } else {
        toast.error(res.error || 'Action failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Action failed');
    } finally {
      setReviewing(false);
    }
  };

  const Row = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-gray-100 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-navy text-right">{value || '—'}</span>
    </div>
  );

  const statusBadge = (s) => {
    const map = {
      pending: { bg: '#FEF3C7', color: '#92400E', label: 'Pending approval' },
      approved: { bg: '#D1FAE5', color: '#065F46', label: 'Approved' },
      rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' }
    };
    const m = map[s] || { bg: '#E5E7EB', color: '#374151', label: s };
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: m.bg, color: m.color }}>{m.label}</span>;
  };

  const FIELD_LABELS = {
    company_name: 'Company Name', company_short_name: 'Short Name',
    company_reg_number: 'Registration Number', company_vat_number: 'VAT Number',
    company_phone: 'Phone', company_email: 'Email', company_website: 'Website',
    company_address: 'Address Line 1', company_address_line2: 'Address Line 2',
    company_logo: 'Logo', bank_account_name: 'Account Name', bank_name: 'Bank',
    bank_account_number: 'Account Number', bank_account_type: 'Account Type',
    bank_branch_code: 'Branch Code'
  };


  return (
    <div className="space-y-6">
      {/* ── Company + bank details (read-only) ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy flex items-center gap-2">
            <FaBuilding className="text-green-600" /> One Purpose Company Profile
          </h3>
          {isAdmin && (
            <button
              onClick={openEdit}
              className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green"
            >
              <FaEdit size={14} /> Edit (passcode required)
            </button>
          )}
        </div>
        {loading ? (
          <div className="py-10 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={22} /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">Company</p>
              <Row label="Company Name" value={profile.company_name} />
              <Row label="Short Name" value={profile.company_short_name} />
              <Row label="Registration Number" value={profile.company_reg_number} />
              <Row label="VAT Number" value={profile.company_vat_number} />
              <Row label="Phone" value={profile.company_phone} />
              <Row label="Email" value={profile.company_email} />
              <Row label="Website" value={profile.company_website} />
              <Row label="Address Line 1" value={profile.company_address} />
              <Row label="Address Line 2" value={profile.company_address_line2} />
              <div className="flex justify-between py-2 border-b border-gray-100 items-center">
                <span className="text-gray-500 text-sm">Logo</span>
                {logoImg ? (
                  <img src={logoImg} alt="Company logo" className="h-10 object-contain" />
                ) : (
                  <span className="text-sm font-medium text-navy">—</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">Bank Details</p>
              <Row label="Account Name" value={profile.bank_account_name} />
              <Row label="Bank" value={profile.bank_name} />
              <Row label="Account Number" value={profile.bank_account_number} />
              <Row label="Account Type" value={profile.bank_account_type} />
              <Row label="Branch Code" value={profile.bank_branch_code} />
              <div className="mt-4 p-3 rounded-xl bg-blue-50 text-blue-700 text-xs flex items-start gap-2">
                <FaShieldAlt className="mt-0.5" size={14} />
                <span>These details appear on every quote, invoice, proforma and credit application. They can only be changed with the company passcode + approval by a second super-admin.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Pending changes / approvals ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
          <FaClipboardCheck className="text-amber-500" /> Company Profile Changes
          {changes.filter((c) => c.status === 'pending').length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              {changes.filter((c) => c.status === 'pending').length} awaiting approval
            </span>
          )}
        </h3>
        {changes.length === 0 ? (
          <p className="text-sm text-gray-400">No company profile changes yet.</p>
        ) : (
          <div className="space-y-3">
            {changes.map((c) => (
              <div key={c.id} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {statusBadge(c.status)}
                    <span className="text-xs text-gray-400">
                      Requested by {c.requester?.full_name || 'admin'} · {new Date(c.requested_at).toLocaleString()}
                    </span>
                  </div>
                  {c.status === 'pending' && isAdmin && (
                    <button
                      onClick={() => { setReviewTarget(c); setReviewPasscode(''); setReviewNotes(''); }}
                      className="px-3 py-1.5 bg-navy text-white rounded-lg text-xs font-medium hover:bg-gray-800"
                    >
                      Review
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {(c.changes || []).map((ch, i) => (
                    <div key={i} className="text-xs">
                      <span className="text-gray-500">{ch.label}: </span>
                      <span className="line-through text-gray-400">{ch.old_value || '—'}</span>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className="font-medium text-green-700">{ch.new_value || '—'}</span>
                    </div>
                  ))}
                </div>
                {c.status !== 'pending' && (
                  <p className="text-xs text-gray-400 mt-2">
                    {c.status === 'approved' ? 'Approved' : 'Rejected'} by {c.approver?.full_name || 'admin'}{c.review_notes ? ` — ${c.review_notes}` : ''}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>


      {/* ── Edit modal ── */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setEditOpen(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy">Edit Company Profile</h2>
              <button onClick={() => setEditOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {FIELD_ORDER.map((k) => (
                <div key={k}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{FIELD_LABELS[k]}</label>
                  <input
                    type="text"
                    value={editData[k] || ''}
                    onChange={(e) => setEditData({ ...editData, [k]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              ))}
            </div>
            <div className="mb-4 p-3 rounded-xl bg-amber-50 text-amber-800 text-sm flex items-start gap-2">
              <FaShieldAlt className="mt-0.5" size={14} />
              <span>Enter the company passcode to submit. A <strong>different</strong> super-admin must then approve the change before it applies.</span>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">Company Passcode</label>
              <input
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Known only to directors + accountant"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditOpen(false)} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitChange} disabled={submitting} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                {submitting ? <FaSpinner className="animate-spin" size={14} /> : <FaCheck size={14} />} Submit for Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Review modal ── */}
      {reviewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setReviewTarget(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-1">Review Company Profile Change</h2>
            <p className="text-xs text-gray-500 mb-4">
              Requested by {reviewTarget.requester?.full_name || 'admin'} · You are approving as a second super-admin.
            </p>
            <div className="space-y-1 mb-4">
              {(reviewTarget.changes || []).map((ch, i) => (
                <div key={i} className="text-sm">
                  <span className="text-gray-500">{ch.label}: </span>
                  <span className="line-through text-gray-400">{ch.old_value || '—'}</span>
                  <span className="mx-1 text-gray-400">→</span>
                  <span className="font-medium text-green-700">{ch.new_value || '—'}</span>
                </div>
              ))}
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">Company Passcode</label>
              <input
                type="password"
                value={reviewPasscode}
                onChange={(e) => setReviewPasscode(e.target.value)}
                placeholder="Directors / accountant passcode"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes (for rejection)</label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows="2"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setReviewTarget(null)} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={() => review('reject')} disabled={reviewing} className="px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 disabled:opacity-50">Reject</button>
              <button onClick={() => review('approve')} disabled={reviewing} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                {reviewing ? <FaSpinner className="animate-spin" size={14} /> : <FaCheck size={14} />} Approve & Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyProfileTab;
